import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, Validators } from '@angular/forms';
import { environment } from '@enviroment/environment';
import { CookieUtil } from '@utils/cookies';
import { UtilsMethods_Services } from '@utils/utils';

@Component({
  selector: 'app-input-phone-international',
  templateUrl: './input-phone-international.component.html',
  styleUrl: './input-phone-international.component.css',
})
export class InputPhoneInternationalComponent implements OnInit {
  @Input() submitted: any;
  @Output() inputPhoneInternational_Emit: EventEmitter<any> = new EventEmitter<any>();
  @Input() phone_number: any;
  @Input() nameInput: any;
  @Input() controls?: FormControl;
  @Input() forceIsRequired?: boolean;
  env = environment;
  options = [
    {
      country: 'Colombia',
      prefix: '+ 57',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/co.svg`,
      validation: (phoneNumber: string) => /^3\d{9}$/.test(phoneNumber), // Colombia: starts with 3, 10 digits
    },
    {
      country: 'Alemania',
      prefix: '+ 49',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/de.svg`,
      validation: (phoneNumber: string) => /^1[5-7]\d{9}$/.test(phoneNumber), // Alemania: starts with 15, 16, or 17, 11 digits total
    },
    {
      country: 'Australia',
      prefix: '+ 61',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/au.svg`,
      validation: (phoneNumber: string) => /^(?:\+614|4)\d{8}$/.test(phoneNumber), // Australia: starts with +61 or 0 and followed by 9 digits
    },
    {
      country: 'Canada',
      prefix: '+ 1',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/canada.svg`,
      // Estrictamente 10 dígitos, formato NPA-NXX-XXXX (sin 0 o 1 en ciertas posiciones)
      validation: (phoneNumber: string) => /^[2-9]\d{2}[2-9]\d{6}$/.test(phoneNumber) && phoneNumber.length === 10,
    },
    {
      country: 'Costa Rica',
      prefix: '+ 506',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/cr.svg`,
      validation: (phoneNumber: string) => /^\+?506\d{8}$/.test(phoneNumber), // Costa Rica: starts with +506 and followed by 8 digits
    },
    {
      country: 'Chile',
      prefix: '+ 56',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/chile.png`,
      validation: (phoneNumber: string) => /^9\d{8}$/.test(phoneNumber), // Chile: starts with 9 and has 9 digits
    },
    {
      country: 'Ecuador',
      prefix: '+ 593',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/ec.svg`,
      validation: (phoneNumber: string) => /^[0-9]\d{8}$/.test(phoneNumber), // Ecuador: 9 dígitos locales
    },
    {
      country: 'Francia',
      prefix: '+ 33',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/fr.svg`,
      validation: (phoneNumber: string) => /^(?:(?:\+33|0033)[1-9]\d{8}|[1-9]\d{8})$/.test(phoneNumber), // Francia: starts with +33 followed by 9 digits or 9 digits without 0
    },
    {
      country: 'Italia',
      prefix: '+ 39',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/it.svg`,
      validation: (phoneNumber: string) => /^3\d{9}$/.test(phoneNumber), // Italia: starts with 3, 10 digits
    },
    {
      country: 'Islandia',
      prefix: '+ 354',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/is.svg`,
      validation: (phoneNumber: string) => /^[6-9]\d{6}$/.test(phoneNumber), // Islandia: starts with 6-9, 7 digits
    },
    {
      country: 'Israel',
      prefix: '+ 972',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/il.svg`,
      validation: (phoneNumber: string) => /^5\d{8}$/.test(phoneNumber), // Israel: starts with 5, 9 digits
    },
    {
      country: 'Malta',
      prefix: '+ 356',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/malta.png`,
      validation: (phoneNumber: string) => /^(?:7|9)\d{6}$/.test(phoneNumber), // Malta: starts with 7 or 9, followed by 6 digits
    },
    {
      country: 'México',
      prefix: '+ 52',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/mx.svg`,
      validation: (phoneNumber: string) => {
        // Para la validación, queremos el número 'puro' después de los prefijos conocidos.
        // El número '15573517371' (11 dígitos) es un celular de México.
        // Un número de 10 dígitos como '5573517371' es un fijo o celular de México.
        const cleaned = phoneNumber.replace(/\s/g, ''); // Eliminar espacios si los hubiera

        // México: 10 dígitos (fijo/celular) O 11 dígitos que comienzan con '1' (celular específico)
        // El '1' es parte del número para la validación si el usuario lo ingresó como 11 dígitos.
        return (
          (/^1\d{10}$/.test(cleaned) && cleaned.length === 11) || // Formato celular de 11 dígitos que empieza con 1
          (/^\d{10}$/.test(cleaned) && cleaned.length === 10)
        ); // Formato general de 10 dígitos
      },
    },
    {
      country: 'España',
      prefix: '+ 34',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/es.svg`,
      validation: (phoneNumber: string) => /^(?:(?:\+|00)34)?\s*[6|7]\d{8}$/.test(phoneNumber), // España: starts with +34 and followed by 9 digits
    },
    {
      country: 'Estados Unidos',
      prefix: '+ 1',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/us.svg`,
      // La validación para Norteamérica es típicamente de 10 dígitos (NPA-NXX-XXXX)
      validation: (phoneNumber: string) => /^[2-9]\d{2}[2-9]\d{6}$/.test(phoneNumber) && phoneNumber.length === 10, // EE.UU. usualmente 10 dígitos, sin 0 o 1 en el 2do o 4to dígito
    },
    {
      country: 'Reino Unido',
      prefix: '+ 44',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/uk.svg`,
      // UK: Accepts numbers starting with 07 or 7, followed by 9 digits
      validation: (phoneNumber: string) => /^(0?7\d{9})$/.test(phoneNumber), // Reino Unido: permite 07871905906 o 7871905906
    },
    {
      country: 'República Dominicana',
      prefix: '+ 1',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/rd.png`,
      placeholder: '(809) 234-5678',
      validation: (phoneNumber: string) => {
        // República Dominicana: 10 dígitos con códigos de área específicos (809, 829, 849)
        const cleaned = phoneNumber.replace(/[\s\-\(\)]/g, ''); // Eliminar espacios, guiones y paréntesis
        return /^(809|829|849)[2-9]\d{6}$/.test(cleaned) && cleaned.length === 10;
      },
    },
    {
      country: 'Portugal',
      prefix: '+ 351',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/pt.svg`,
      validation: (phoneNumber: string) => /^\d{9}$/.test(phoneNumber), // Portugal: starts with +351 and followed by 9 digits
    },
    {
      country: 'Polonia',
      prefix: '+ 48',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/pl.svg`,
      validation: (phoneNumber: string) => /^[1-9]\d{8}$/.test(phoneNumber), // Polonia: starts with +48 and followed 9 dígitos, no comienza con 0
    },
    {
      country: 'Luxemburgo',
      prefix: '+ 352',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/lu.svg`,
      validation: (phoneNumber: string) => /^6\d{8}$/.test(phoneNumber), // Luxemburgo: comienza con 6 y tiene 9 dígitos (móviles)
    },
    {
      country: 'Arabia Saudita',
      prefix: '+ 966',
      image: `${environment.S3_STATIC_FILES}/images/web/countrys/sa.svg`,
      validation: (phoneNumber: string) => /^5\d{8}$/.test(phoneNumber), // Arabia Saudita: comienza con 5 y tiene 9 dígitos
    },
  ];

  selectedOption: any = this.options[0];
  search: string = '';
  isOpen = false;

  constructor(
    public cookies: CookieUtil,
    public utils: UtilsMethods_Services,
    private elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    if (this.env.BRAND != 'mcdt') {
      this.loadOnlyCO();
    }
    let cookie_phone = this.cookies.get('phone-number');
    const prefix_phone_cookie = this.utils.deleteSpace(this.cookies.get('phone-prefix'));
    if (!this.phone_number) {
      if (cookie_phone) {
        this.phone_number = cookie_phone;

        if (prefix_phone_cookie && prefix_phone_cookie !== '') {
          this.selectedOption = this.options.find(
            (option) => this.utils.deleteSpace(option.prefix) === prefix_phone_cookie,
          );
        }
      } else {
        this.phone_number = '';
        this.selectedOption = this.options[0]; // Default a la primera opción
      }
    } else {
      const result = this.extractPhoneNumberWithoutPrefix(this.phone_number);
      this.phone_number = result.phoneNumber;
      this.selectedOption = result.selectedOption;
    }

    if (!this.nameInput) {
      this.nameInput = 'phone_number_international';
    } else if (this.nameInput == 'working_info.company_phone' && this.phone_number === cookie_phone) {
      this.phone_number = '';
    }
  }

  toggleOptions() {
    if (this.env.BRAND == 'mcdt') {
      this.isOpen = !this.isOpen;
    }
  }

  selectOption(country: string) {
    this.selectedOption = this.options.find((option) => option.country === country);
    this.isOpen = false;
    const prefix_phone_cookie = this.cookies.get('phone-prefix');
    if (this.phone_number && (prefix_phone_cookie == 'undefined' || !prefix_phone_cookie)) {
      this.validatePhoneNumber();
    }
  }

  validatePhoneNumber() {
    let number_remove_space = this.utils.deleteSpace(this.phone_number);
    let prefix = this.utils.deleteSpace(this.selectedOption.prefix);

    // Verifica si el campo es requerido
    const isRequired = this.controls?.hasValidator(Validators.required) || this.forceIsRequired;

    // Realiza la validación si es requerido, de lo contrario, siempre retorna true
    const isValid = isRequired ? this.selectedOption.validation(number_remove_space) : true;

    let phone_full = `${this.selectedOption.prefix} ${number_remove_space}`;

    const emit = {
      validate: isValid,
      phone_number: number_remove_space,
      phone_full: phone_full,
      prefix: prefix,
    };

    this.inputPhoneInternational_Emit.emit(emit);
    return isValid;
  }

  loadOnlyCO(): void {
    this.options = this.options.filter((option) => option.country === 'Colombia');
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  filterOptions() {
    return this.options.filter((option) => option.country.toLowerCase().includes(this.search.toLowerCase()));
  }

  extractPhoneNumberWithoutPrefix(phoneNumber: string): { phoneNumber: string; selectedOption: any } {
    phoneNumber = phoneNumber.replace(/\s+/g, ''); // Remover espacios en blanco

    let bestMatch: { phoneNumber: string; selectedOption: any } | null = null;

    // Recorrer las opciones para encontrar la mejor coincidencia basada en la longitud del prefijo
    for (const option of this.options) {
      const prefixWithPlus = option.prefix.replace(/\s+/g, ''); // Prefijo con el '+'
      const prefixWithoutPlus = prefixWithPlus.replace('+', ''); // Prefijo sin el '+'

      if (phoneNumber.startsWith(prefixWithPlus)) {
        const cleanedPhoneNumber = phoneNumber.slice(prefixWithPlus.length); // Cortar el prefijo
        bestMatch = { phoneNumber: cleanedPhoneNumber, selectedOption: option };
        break; // Romper el ciclo una vez que encontremos el prefijo adecuado
      } else if (phoneNumber.startsWith(prefixWithoutPlus)) {
        const cleanedPhoneNumber = phoneNumber.slice(prefixWithoutPlus.length); // Cortar el prefijo
        bestMatch = { phoneNumber: cleanedPhoneNumber, selectedOption: option };
        break; // Romper el ciclo una vez que encontremos el prefijo adecuado
      }
    }

    // Si encuentra la mejor coincidencia, la devuelve, si no, devuelve el número completo y la primera opción
    return bestMatch || { phoneNumber, selectedOption: this.options[0] };
  }
}
